import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bus, Home as HomeIcon, Repeat, Check, Sparkles } from "lucide-react";

const COMPARE = [
  { label: "Day Pass", early: "$28", regular: "$30" },
  { label: "Week Pass", early: "$115", regular: "$125" },
  { label: "Month Pass", early: "$425", regular: "$450" },
  { label: "Gold Membership", early: "$50", regular: "$75", note: "Term 1 Special" },
];

const PASSES = [
  { name: "Daily Pass", program: "$30", oneway: "$42", round: "$54" },
  { name: "Weekly Pass", program: "$125", oneway: "$175", round: "$225" },
  { name: "Monthly Pass", program: "$450", oneway: "$650", round: "$850", note: "up to 20 program days" },
];

const TRANSPORT = [
  { icon: Bus, label: "School Pick-Up", price: "$12", unit: "/ day" },
  { icon: HomeIcon, label: "Home Drop-Off", price: "$12", unit: "/ day" },
  { icon: Repeat, label: "Round Trip", price: "$24", unit: "/ day" },
];

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Pricing() {
  const [live, setLive] = useState(false);
  useEffect(() => {
    document.title = "Pricing — Adventure Rock Bermuda";
    api.get("/public/settings").then((r) => setLive(!!r.data.registration_live)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy min-h-screen text-white overflow-x-hidden">
      <Navbar />

      <section className="max-w-5xl mx-auto px-4 pt-28 pb-10 text-center">
        <motion.p {...fade} className="text-turquoise font-bold tracking-[0.3em] text-xs">AFTER-SCHOOL PROGRAM</motion.p>
        <motion.h1 {...fade} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-2">Adventure Rock <span className="text-gold">Pricing</span></motion.h1>
        <motion.p {...fade} className="text-white/70 mt-4 max-w-2xl mx-auto">Simple passes, honest pricing. Lock in our Early Bird rates before pre-registration opens on <b className="text-turquoise">September 25</b>.</motion.p>
        <motion.div {...fade} className="inline-flex items-center gap-2 mt-6 bg-gold text-navy font-black rounded-full px-5 py-2 text-sm" data-testid="earlybird-banner">
          <Sparkles className="w-4 h-4" /> LIMITED EARLY BIRD OFFER
        </motion.div>
      </section>

      {/* Early Bird vs Regular */}
      <section className="max-w-4xl mx-auto px-4 grid md:grid-cols-2 gap-5">
        <motion.div {...fade} className="rounded-3xl border-2 border-gold bg-gold/5 p-6" data-testid="pricing-earlybird">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-extrabold text-2xl text-gold">Early Bird Special</h2>
            <span className="text-[10px] font-bold bg-gold text-navy rounded-full px-2 py-1">BEFORE SEPT 25</span>
          </div>
          <div className="mt-4 divide-y divide-white/10">
            {COMPARE.map((r) => (
              <div key={r.label} className="flex items-center justify-between py-3">
                <span className="text-white/85">{r.label}{r.note && <span className="block text-[11px] text-gold/80">{r.note}</span>}</span>
                <span className="font-display font-black text-2xl text-gold">{r.early}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fade} className="rounded-3xl border-2 border-turquoise/40 bg-white/5 p-6" data-testid="pricing-regular">
          <h2 className="font-display font-extrabold text-2xl text-turquoise">Regular Pricing</h2>
          <div className="mt-4 divide-y divide-white/10 md:mt-[52px]">
            {COMPARE.map((r) => (
              <div key={r.label} className="flex items-center justify-between py-3">
                <span className="text-white/85">{r.label}</span>
                <span className="font-display font-black text-2xl text-white">{r.regular}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Transport rates */}
      <section className="max-w-4xl mx-auto px-4 mt-14">
        <motion.h2 {...fade} className="font-display font-extrabold text-3xl text-center">Add Transportation</motion.h2>
        <motion.p {...fade} className="text-white/70 text-center mt-2">Safe, staffed mini-bus service — charged per day, separate from your pass.</motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          {TRANSPORT.map((t) => (
            <motion.div key={t.label} {...fade} className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center" data-testid={`transport-rate-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="w-12 h-12 rounded-xl bg-turquoise/15 flex items-center justify-center mx-auto"><t.icon className="w-6 h-6 text-turquoise" /></div>
              <p className="mt-3 font-semibold text-white/85">{t.label}</p>
              <p className="font-display font-black text-3xl text-gold mt-1">{t.price}<span className="text-sm text-white/50 font-normal"> {t.unit}</span></p>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-white/50 text-xs mt-3">One-way transport = school pick-up <b>or</b> home drop-off.</p>
      </section>

      {/* Passes with transport */}
      <section className="max-w-5xl mx-auto px-4 mt-14">
        <motion.h2 {...fade} className="font-display font-extrabold text-3xl text-center">Passes With Transport</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {PASSES.map((p) => (
            <motion.div key={p.name} {...fade} className="rounded-3xl bg-gradient-to-br from-navy to-ocean border border-turquoise/20 p-6" data-testid={`pass-${p.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <h3 className="font-display font-extrabold text-xl text-turquoise">{p.name}</h3>
              {p.note && <p className="text-[11px] text-white/50 mb-2">{p.note}</p>}
              <div className="mt-3 space-y-2">
                <Row label="Program only" value={p.program} />
                <Row label="+ One-way transport" value={p.oneway} />
                <Row label="+ Round-trip transport" value={p.round} highlight />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Fine print + CTA */}
      <section className="max-w-3xl mx-auto px-4 mt-14 text-center">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-left text-white/60 text-xs space-y-1">
          <p className="flex items-start gap-2"><Check className="w-4 h-4 text-turquoise mt-0.5 shrink-0" /> Monthly pass covers up to 20 program days.</p>
          <p className="flex items-start gap-2"><Check className="w-4 h-4 text-turquoise mt-0.5 shrink-0" /> Adventure Credits are valid 30 days or until the current term ends, whichever comes first.</p>
          <p className="flex items-start gap-2"><Check className="w-4 h-4 text-turquoise mt-0.5 shrink-0" /> Transportation is subject to route and space availability; a staff member rides along on every route.</p>
        </div>
        <h3 className="font-display font-extrabold text-2xl mt-10">{live ? "Ready to secure your spot?" : "Want the Early Bird rate?"}</h3>
        <p className="text-white/70 mt-2">{live ? "Pre-registration is open — book your explorer's place today." : "Join the mailing list and be the first to know the moment pre-registration opens on September 25."}</p>
        <Link to={live ? "/register" : "/join"}>
          <Button className="mt-6 bg-gold hover:bg-gold/90 text-navy font-black rounded-full text-base px-8 py-6" data-testid="pricing-cta">
            {live ? "Pre-Register Now" : "Join the Mailing List"}
          </Button>
        </Link>
      </section>

      <div className="mt-16"><Footer /></div>
    </div>
  );
}

const Row = ({ label, value, highlight }) => (
  <div className={`flex items-center justify-between rounded-xl px-3 py-2 ${highlight ? "bg-gold/10" : "bg-white/5"}`}>
    <span className="text-sm text-white/80">{label}</span>
    <span className={`font-display font-black text-xl ${highlight ? "text-gold" : "text-white"}`}>{value}</span>
  </div>
);
